import React, { useState, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import Box from '@mui/material/Box';

import Typography from 'src/components/ui/Typography';
import SimpleHorizontalUserCard from 'src/components/ui/SimpleHorizontalUserCard';
import Icon from 'src/components/ui/Icon';
import Loader from 'src/components/ui/Loader';

import type { CompanyProfileQuery, User } from 'src/types/__generated__/graphql';
import { colours } from 'src/constants/colours';
import { isBottomDiv } from 'src/utils/isBottom';
import { useFollowersQuery } from 'src/hooks/__generated__/queries';
import useUser from 'src/hooks/useUser';
import useAlerts from 'src/hooks/useAlerts';
import FollowUnFollowButton from 'src/components/ui/FollowUnFollowButton';

interface Props {
    company: NonNullable<CompanyProfileQuery['companyProfile']>;
}

const defaultTake = 10;
const FollowModalCompanyContent: React.FC<Props> = ({ company }) => {
    const { t } = useTranslation(['company', 'common']);
    const currentUser = useUser();
    const { addAlert } = useAlerts();
    const scrollerContainerRef = useRef<HTMLDivElement | null>(null);

    const { data, fetchMore, loading } = useFollowersQuery({
        variables: {
            companyId: company.id,
            limit: defaultTake,
            offset: 0,
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'cache-and-network',
    });

    const allDataLoaded = Boolean(data && data.followers.results.length >= data.followers.total);

    const handleScroll = async (ev: React.UIEvent<HTMLDivElement, UIEvent>) => {
        const bottom = isBottomDiv(ev.currentTarget);
        if (bottom && !loading && !allDataLoaded) {
            try {
                await fetchMore({
                    variables: {
                        offset: data?.followers.results.length,
                    },
                });
            } catch (error: unknown) {
                addAlert({
                    type: 'error',
                    message: (error as Error).message,
                });
            }
        }
    };

    return (
        <Box display="flex" flexDirection="column" height="100%">
            <Box
                ref={scrollerContainerRef}
                display="flex"
                flexDirection="column"
                flex={1}
                overflow="auto"
                onScroll={handleScroll}
                paddingX="1.5rem"
            >
                {!data?.followers.results.length && !loading && (
                    <Box
                        display="flex"
                        flexDirection="column"
                        height="100%"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Icon color={colours.primary.main} size="7.5rem" name="FollowersGradient" />
                        <Box mt={2}>
                            <Typography
                                variant="body2"
                                align="center"
                                data-target-id="follow-modal-company-content-empty"
                            >
                                {t('company:companyFollowersEmptyState')}
                            </Typography>
                        </Box>
                    </Box>
                )}
                {data?.followers.results.map((follow) => (
                    <div key={follow.id}>
                        <SimpleHorizontalUserCard
                            dataTargetId={`simple-horizontal-card-${follow.followingUser.id}`}
                            user={follow.followingUser as User}
                            firstSubtitle={
                                follow.followingUser.isProfessionnal
                                    ? null // send null to not display activity in this case
                                    : undefined
                            }
                            secondSubtitle={
                                follow.followingUser.id === currentUser?.id
                                    ? undefined // send undefined to not display common relations for current user
                                    : 'commonRelationsCount'
                            }
                            minHeight="5rem"
                            padding="0.5rem 1rem"
                            backgroundColor="transparent"
                            rightArea={
                                <FollowUnFollowButton user={follow.followingUser} refetchQueries={['CompanyProfile']} />
                            }
                        />
                    </div>
                ))}
                {loading && <Loader />}
            </Box>
        </Box>
    );
};

export default FollowModalCompanyContent;
